import { useCallback, useEffect } from 'react';
import { useQuery } from '@apollo/client';

import { ShineStartDocument } from '__generated__/GQL';
import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import { apolloClient } from 'common/graphql/clients';
import useCompanyContext from 'common/hooks/useCompanyContext';
import webView from 'common/webView';
import SuspenseQuery from 'components/SuspenseQuery';

import OBLoaderPage from '../../components/OBLoaderPage/OBLoaderPage';
import {
  ShineStartVerificationModuleDataDocument,
  ShineStartVerificationModuleDataQuery,
} from './graphql/queries/shineStartVerificationModuleData.gql';
import getSelectedDocument from './libs/getSelectedDocument';
import machineConfig, { model, State } from './machine';
import { ShineStartVerificationModuleData } from './machine/model';
import DocumentRefused from './pages/DocumentRefused';
import DocumentUpload from './pages/DocumentUpload';
import HandwrittenIdentityInfo from './pages/HandwrittenIdentityInfo';
import HandwrittenIdentityRefused from './pages/HandwrittenIdentityRefused';
import HandwrittenIdentityUpload from './pages/HandwrittenIdentityUpload';
import Home from './pages/Home';
import Intro from './pages/Intro';

const isModuleDataValid = (
  data: ShineStartVerificationModuleDataQuery,
): data is ShineStartVerificationModuleData['data'] =>
  Boolean(data.viewer.company.shineStart);

const ShineStartVerificationComponent = withStateMachine<typeof model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;
    const { viewer } = moduleData.data;

    const stringifiedState = stringifyState<State>(state);

    const onPrev = useCallback(() => send(model.events.PREV()), [send]);

    switch (stringifiedState) {
      case State.INTRO: {
        return (
          <Intro onNext={() => send(model.events.NEXT())} onPrev={onPrev} />
        );
      }

      case State.HOME: {
        return (
          <Home
            documents={
              viewer.company.shineStart.documents as ShineStartDocument[]
            }
            onDocumentClick={(documentId) =>
              send(model.events.SELECT_DOCUMENT(documentId))
            }
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );
      }

      case State.DOCUMENT_REFUSED: {
        const { selectedDocumentId } = state.context;

        const selectedDocument = getSelectedDocument({
          moduleData,
          selectedDocumentId,
        });

        if (!selectedDocument) {
          throw Error(
            `Cannot access ${state} state without a selected document`,
          );
        }

        return (
          <DocumentRefused
            document={selectedDocument}
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );
      }

      case State.DOCUMENT_UPLOAD: {
        const { selectedDocumentId } = state.context;

        const selectedDocument = getSelectedDocument({
          moduleData,
          selectedDocumentId,
        });

        if (!selectedDocument) {
          throw Error(
            `Cannot access ${state} state without a selected document`,
          );
        }

        return (
          <DocumentUpload
            document={selectedDocument}
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );
      }

      case State.HANDWRITTEN_IDENTITY_INFO: {
        return (
          <HandwrittenIdentityInfo
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );
      }

      case State.HANDWRITTEN_IDENTITY_REFUSED: {
        const { selectedDocumentId } = state.context;

        const selectedDocument = getSelectedDocument({
          moduleData,
          selectedDocumentId,
        });

        if (!selectedDocument) {
          throw Error(
            `Cannot access ${state} state without a selected document`,
          );
        }

        return (
          <HandwrittenIdentityRefused
            document={selectedDocument}
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );
      }

      case State.HANDWRITTEN_IDENTITY_UPLOAD: {
        const { selectedDocumentId } = state.context;

        const selectedDocument = getSelectedDocument({
          moduleData,
          selectedDocumentId,
        });

        if (!selectedDocument) {
          throw Error(
            `Cannot access ${state} state without a selected document`,
          );
        }

        return (
          <HandwrittenIdentityUpload
            document={selectedDocument}
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );
      }

      case State.INIT:
      case State.HANDWRITTEN_IDENTITY_INIT:
      case State.DOCUMENT_INIT:
      case State.ABORT:
      case State.DONE:
        return <OBLoaderPage onPrev={() => null} />;

      default:
        return assertNever(stringifiedState);
    }
  },

  { devTools: true, machineConfig },
);

interface ShineStartVerificationProps {
  companyProfileId: string;
}
const ShineStartVerification = asBentoModule<ShineStartVerificationProps>(
  ({ companyProfileId, onDone }) => {
    const query = useQuery(ShineStartVerificationModuleDataDocument, {
      notifyOnNetworkStatusChange: true,
      variables: {
        companyProfileId,
      },
    });

    useEffect(() => {
      return webView.listenNativeMessage((message) => {
        if (message.action === 'NATIVE_ID_CHECK_COMPLETED') {
          apolloClient.refetchQueries({
            include: [ShineStartVerificationModuleDataDocument],
          });
        }
      });
    }, []);

    const companyContext = useCompanyContext();

    return (
      <SuspenseQuery
        loader={<OBLoaderPage onPrev={() => null} />}
        query={query}
      >
        {(data) => {
          if (!isModuleDataValid(data)) {
            throw 'Shine Start cannot be null';
          }

          return (
            <ShineStartVerificationComponent
              moduleData={{
                companyContext,
                data: data,
              }}
              onDone={onDone}
            />
          );
        }}
      </SuspenseQuery>
    );
  },
);

export default ShineStartVerification;
